import { AccoladeTooltip, SellerAccolade } from '@c/seller/Accolades';
import { ReviewDocument } from '@models/review';
import { useQuery } from '@tanstack/react-query';
import Avatar from '@ui/Avatar';
import StarRating from '@ui/StarRating';
import { getPublicUserDoc } from '@util/firestore/users';
import { cn } from '@util/index';
import Link from 'next/link';

const ReviewCard = ({
  review,
  showBadges,
  hideAvatar,
  grow,
  className,
  widthClassName,
}: {
  review: ReviewDocument;
  showBadges?: boolean;
  hideAvatar?: boolean;
  grow?: boolean;
  className?: string;
  widthClassName?: string;
}) => {
  const { data: sellerDoc } = useQuery({
    queryKey: ['publicUser', review.uid],
    queryFn: () =>
      getPublicUserDoc({
        uid: review.uid,
      }),
  });
  return (
    <div
      className={cn(
        'mb-4 flex flex-col gap-[1.6rem] rounded-[2rem] bg-brand-white p-[1.6rem] shadow-sm sm:w-[35rem]',
        {
          'min-h-[19.5rem]': grow,
          'h-[19.5rem]': !grow,
        },
        className,
        widthClassName ? widthClassName : 'w-[calc(100vw_-_2.5rem)]'
      )}
    >
      <div className="flex h-touch gap-[0.8rem]">
        <StarRating rating={review.rating} className="h-[3rem] w-[3rem]" />
      </div>
      {!grow ? (
        <p className="grow text-input ">
          {review.comment?.slice(0, 80)}
          {review.comment &&
            review.comment.length > 80 &&
            sellerDoc?.username_slug && (
              <Link
                href={`/profile/${sellerDoc.username_slug}`}
                className="font-semibold text-brand-secondary"
              >
                ...Read more
              </Link>
            )}
          {!review.comment && <i>No comment left in review. </i>}
        </p>
      ) : (
        <p className="grow text-input ">{review.comment}</p>
      )}

      {showBadges && (
        <div className="flex gap-[0.8rem]">
          <SellerAccolade
            accolade="quick_responder"
            count={review.quick_replies ? 10 : 0}
            isProfilePage
            iconOnly
          />
          <SellerAccolade
            accolade="accurate_describer"
            count={review.as_described ? 10 : 0}
            isProfilePage
            iconOnly
          />
          <SellerAccolade
            accolade="fast_shipper"
            count={review.fast_shipper ? 10 : 0}
            isProfilePage
            iconOnly
          />
          <AccoladeTooltip />
        </div>
      )}

      {!hideAvatar && (
        <div className="flex w-full flex-col items-start justify-between gap-y-2 sm:flex-row sm:items-center">
          <div className="flex min-w-0 items-center gap-[0.8rem]">
            <Avatar
              user={{
                username: review.reviewer_username,
                photo: review.reviewer_photo,
                is_verified: false,
              }}
              size="extraSmall"
            />
            <Link
              href={`/profile/${review.reviewer_username}`}
              className="truncate text-[1.5rem] font-semibold underline"
            >
              {review.reviewer_username}
            </Link>
          </div>

          <div className="flex shrink-0 text-[1.4rem] font-medium text-zinc-500">
            {review.created &&
              new Date(review.created).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewCard;
